/**
 * React provider + hooks for @clearpath/auth-client.
 *
 * Pattern: a single AuthClient instance is created at app init (typically in
 * `main.tsx`) and passed to <AuthProvider client={client}>. Consumers call
 * useAuth() to read session state + trigger sign-in / sign-out / switch.
 *
 * The provider subscribes to client.onSessionChange so multiple components
 * stay in sync after a tenant switch / silent refresh / step-up MFA.
 */
import type { JSX } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { AuthClient, SignInOptions } from "../client.js";
import type { SignOutOptions } from "../sign-out-flow.js";
import type { SignOutResult } from "../sign-out.js";
import type { Session, Tenant } from "../types.js";

export interface AuthContextValue {
  /** Current session, or null if not authenticated. */
  session: Session | null;
  /** True until the initial cold-load /me call resolves. */
  loading: boolean;
  /** Underlying client (for advanced use: MFA helpers, tenant list, etc.). */
  client: AuthClient;
  /** Start sign-in (PKCE redirect). */
  signIn: (options?: SignInOptions) => Promise<void>;
  /** Sign out + redirect to postLogoutUri. */
  signOut: (options?: SignOutOptions) => Promise<SignOutResult>;
  /** Switch tenants via silent refresh. */
  switchTenant: (tenantId: string) => Promise<Session>;
  /** List tenants the current user can switch to. */
  listTenantMemberships: () => Promise<Tenant[]>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  client: AuthClient;
  children: ReactNode;
  /**
   * If true, skip the initial getCurrentUser() cold-load. Useful in tests
   * or when the consumer pre-hydrates state via SSR. Default: false.
   */
  skipColdLoad?: boolean;
}

export function AuthProvider({
  client,
  children,
  skipColdLoad = false,
}: AuthProviderProps): JSX.Element {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(!skipColdLoad);

  // Subscribe to session changes from the client (silent refresh, switch
  // tenant, sign-out from another tab, etc.).
  useEffect(() => {
    const unsubscribe = client.onSessionChange((s) => setSession(s));
    return unsubscribe;
  }, [client]);

  // Cold-load on mount.
  useEffect(() => {
    if (skipColdLoad) return;
    let cancelled = false;
    void (async () => {
      const current = await client.getCurrentUser();
      if (cancelled) return;
      // A session can be established via `onSessionChange` (the OIDC callback's
      // `handleCallback`, or a silent refresh) WHILE this one-shot cold-load is
      // still in flight. The cold-load exists only to RESTORE a session on the
      // first paint — it must never CLOBBER a live session that arrived in the
      // meantime. On a fresh first-login the cold-load resolves to `null` — no
      // host-only refresh cookie exists yet on the auth host; it's only set by
      // the `/token` exchange `handleCallback` performs. If that late `null`
      // overwrote the session `handleCallback` just adopted, the app would flip back to
      // `!session` and bounce the user to sign-in (an infinite callback loop
      // that only a manual reload — where `getCurrentUser` now finds the stored
      // user and returns a session — breaks out of). This was reachable on any
      // SPA but became deterministic on analytics, whose FlagProvider issues a
      // second `getAccessToken()`-driven cold-load during the callback (ENG-1753).
      // So only adopt the cold-load result when it produced a session; a `null`
      // keeps whatever `onSessionChange` has already set.
      setSession((prev) => current ?? prev);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [client, skipColdLoad]);

  const signIn = useCallback(
    (options?: SignInOptions) => client.signIn(options),
    [client],
  );
  // Preserve the structured outcome so idle-guard consumers can report a
  // server-side sign-out failure. UI/retry handling remains a separate concern.
  const signOut = useCallback(
    (options?: SignOutOptions) => client.signOut(options),
    [client],
  );
  const switchTenant = useCallback(
    (tenantId: string) => client.switchTenant(tenantId),
    [client],
  );
  const listTenantMemberships = useCallback(
    () => client.listTenantMemberships(),
    [client],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      loading,
      client,
      signIn,
      signOut,
      switchTenant,
      listTenantMemberships,
    }),
    [session, loading, client, signIn, signOut, switchTenant, listTenantMemberships],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth() must be used inside <AuthProvider>");
  return ctx;
}

/**
 * Guard hook: redirects to sign-in if not authenticated once cold-load
 * resolves. Returns the session (or null while loading). Components that
 * want to render a skeleton during loading should check `loading` first.
 */
export function useRequireAuth(options?: SignInOptions): Session | null {
  const { session, loading, signIn } = useAuth();
  useEffect(() => {
    if (!loading && !session) {
      void signIn(options);
    }
    // We intentionally don't include `options` to avoid redirect loops on
    // re-render — sign-in only fires once per loading→unauthenticated edge.
    // (No eslint-disable needed: react-hooks/exhaustive-deps is 'off' for
    // packages/**/*.tsx in the root eslint.config.js, so the directive was
    // itself a --max-warnings=0 failure.)
  }, [loading, session, signIn]);
  return session;
}
